import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Flag, Loader2, ArrowLeft, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Public permalink for a restaurant.
 *
 * Behaviour:
 *  - If the place is currently active → redirect to `/?p={placeId}` so the
 *    existing details sheet opens on top of the map.
 *  - If the place has been hidden by an admin (or removed via a community
 *    report) → render a transparency page explaining why, instead of a
 *    confusing 404.
 */
export default function RestaurantPermalink() {
  const { placeId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, info: null, error: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(
          `/restaurants/${encodeURIComponent(placeId)}/transparency`
        );
        if (cancelled) return;
        if (!data.is_hidden) {
          // Live listing — bounce to the main map with the details sheet auto-open.
          navigate(`/?p=${encodeURIComponent(placeId)}`, { replace: true });
          return;
        }
        setState({ loading: false, info: data, error: null });
      } catch (err) {
        if (cancelled) return;
        setState({
          loading: false,
          info: null,
          error: err?.response?.data?.detail || "Couldn't load this listing",
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [placeId, navigate]);

  if (state.loading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-[#FDFBF7]">
        <Loader2 className="w-5 h-5 animate-spin text-[#D95D39]" />
      </div>
    );
  }

  if (state.error) {
    return (
      <Shell>
        <h1 className="font-display text-3xl text-[#2A3B2C]">
          Listing unavailable
        </h1>
        <p className="text-sm text-[#647466] mt-2">{state.error}</p>
      </Shell>
    );
  }

  const info = state.info;
  const formattedDate = info.hidden_at
    ? new Date(info.hidden_at).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  return (
    <Shell>
      <div
        data-testid="hidden-transparency-page"
        className="rounded-3xl bg-white border border-[#E5E1D8] p-7 sm:p-10"
      >
        <div className="w-14 h-14 rounded-full bg-[#FFF3EE] flex items-center justify-center">
          <Flag className="w-6 h-6 text-[#D95D39]" />
        </div>
        <h1 className="mt-5 font-display text-2xl sm:text-3xl text-[#2A3B2C] leading-tight">
          {info.restaurant_name
            ? `${info.restaurant_name} has been removed from AlKoncierge`
            : "This listing has been removed from AlKoncierge"}
        </h1>
        <p className="mt-3 text-[15px] leading-relaxed text-[#647466]">
          {info.hidden_via === "report" ? (
            <>
              The community flagged this restaurant and our team confirmed the
              report. We&apos;ve hidden the listing while concerns are addressed.
            </>
          ) : (
            <>
              An AlKoncierge admin hid this restaurant from the public map.
              This is a moderation decision and not a reflection of any
              individual diner&apos;s experience.
            </>
          )}
        </p>

        {info.reason_label && (
          <div
            data-testid="hidden-reason"
            className="mt-5 rounded-2xl bg-[#F2EDE3] border border-[#E5E1D8] p-4"
          >
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#647466]">
              Reason
            </div>
            <div className="mt-1 font-display text-lg text-[#2A3B2C]">
              {info.reason_label}
            </div>
            {formattedDate && (
              <div className="mt-0.5 text-xs text-[#8A9A8B]">
                Hidden on {formattedDate}
              </div>
            )}
          </div>
        )}

        <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Link
            to="/"
            data-testid="back-to-map-btn"
            className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full bg-[#D95D39] hover:bg-[#C54E2C] text-white text-sm font-medium"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Find another halal spot
          </Link>
          <a
            href="mailto:hello@alkoncierge.com?subject=Listing%20removed%20-%20appeal"
            data-testid="appeal-link"
            className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full bg-white border border-[#E5E1D8] hover:border-[#2A3B2C] text-[#2A3B2C] text-sm font-medium"
          >
            <ShieldCheck className="w-3.5 h-3.5" />
            I&apos;m the owner — appeal this
          </a>
        </div>

        <p className="mt-6 text-[11px] text-[#8A9A8B]">
          AlKoncierge surfaces halal restaurants from public sources and
          community submissions. We may hide listings while we verify a
          credible concern. We restore them as soon as the issue is resolved.
        </p>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen w-full bg-[#FDFBF7] flex items-start justify-center px-4 py-12 sm:py-20">
      <div className="w-full max-w-xl">{children}</div>
    </div>
  );
}
